const VOUCHER_DEBUG =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_VOUCHER_DEBUG === 'true';

const VOUCHER_STORAGE_KEY = 'auth_voucher_state';
const VOUCHER_STORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const VOUCHER_PARAM_KEYS = [
  'voucher',
  'coupon',
  'couponCode',
  'coupon_code',
  'code',
  'promo',
  'promoCode',
  'promotionCode',
  'promotion_code',
];

const COUPON_ID_PARAM_KEYS = ['couponId', 'coupon_id'];
const PROMO_ID_PARAM_KEYS = ['promotionCodeId', 'promotion_code_id', 'promo_id', 'promotion_id'];

export const logVoucherDebug = (label, payload) => {
  if (!VOUCHER_DEBUG) return;
  if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.log(`[VoucherDebug] ${label}`, payload);
  }
};

export const normalizeVoucherValue = value => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
};

const codesMatch = (a, b) => {
  const left = normalizeVoucherValue(a);
  const right = normalizeVoucherValue(b);
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
};

const readFirstParam = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value !== null && value !== undefined && String(value).trim().length > 0) {
      return value;
    }
  }
  return null;
};

export const parseVoucherParams = searchOrParams => {
  if (typeof searchOrParams === 'undefined' || searchOrParams === null) return {};

  const params =
    searchOrParams instanceof URLSearchParams
      ? searchOrParams
      : new URLSearchParams(searchOrParams || '');

  const voucher = normalizeVoucherValue(readFirstParam(params, VOUCHER_PARAM_KEYS));
  const couponId = normalizeVoucherValue(readFirstParam(params, COUPON_ID_PARAM_KEYS));
  const promotionCodeId = normalizeVoucherValue(readFirstParam(params, PROMO_ID_PARAM_KEYS));

  const result = {
    voucher,
    couponCode: voucher,
    couponId,
    promotionCodeId,
  };

  logVoucherDebug('Parsed voucher params', {
    search: typeof searchOrParams === 'string' ? searchOrParams : undefined,
    result,
  });

  return result;
};

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage || null;
};

export const readVoucherStorage = () => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(VOUCHER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const storedAt = parsed?.updatedAt || 0;
    if (storedAt && Date.now() - storedAt > VOUCHER_STORAGE_TTL_MS) {
      storage.removeItem(VOUCHER_STORAGE_KEY);
      return null;
    }
    const voucher = normalizeVoucherValue(parsed?.voucher);
    if (!voucher) return null;
    return { voucher, source: parsed?.source || 'storage', updatedAt: storedAt };
  } catch (error) {
    return null;
  }
};

export const writeVoucherStorage = (voucher, source = 'input') => {
  const storage = getStorage();
  if (!storage) return;

  const normalized = normalizeVoucherValue(voucher);
  if (!normalized) {
    storage.removeItem(VOUCHER_STORAGE_KEY);
    return;
  }

  const payload = {
    voucher: normalized,
    source,
    updatedAt: Date.now(),
  };

  try {
    storage.setItem(VOUCHER_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore storage errors
  }
};

export const clearVoucherStorage = () => {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(VOUCHER_STORAGE_KEY);
};

export const resolveVoucherInput = ({ inputVoucher, queryVoucher, storedVoucher } = {}) => {
  const input = normalizeVoucherValue(inputVoucher);
  if (input) return { voucher: input, source: 'input' };

  const query = normalizeVoucherValue(queryVoucher);
  if (query) return { voucher: query, source: 'query' };

  const stored = normalizeVoucherValue(storedVoucher);
  if (stored) return { voucher: stored, source: 'storage' };

  return { voucher: null, source: null };
};

export const buildVoucherFields = ({ voucher, couponId = null, couponCode = null } = {}) => {
  const normalizedVoucher = normalizeVoucherValue(voucher);
  if (!normalizedVoucher) {
    return { voucher: null, couponCode: null, couponId: null };
  }

  return {
    voucher: normalizedVoucher,
    couponCode: couponCode || normalizedVoucher,
    couponId: couponId || null,
  };
};

export const deriveVoucherFields = ({
  voucherInput,
  verifyState = {},
  requireVerified = false,
} = {}) => {
  const normalizedVoucher = normalizeVoucherValue(voucherInput);
  const hasInput = Boolean(normalizedVoucher);

  const {
    success,
    error,
    isCodeValid,
    couponId,
    couponCode,
    promotionCodeId,
    isContentVoucher,
  } = verifyState || {};

  const verifiedCodeMatches = codesMatch(couponCode, normalizedVoucher);
  const isVerified = Boolean(hasInput && success && isCodeValid && verifiedCodeMatches);
  const isInvalid = Boolean(hasInput && error);

  const shouldAttach = requireVerified ? isVerified : hasInput;

  const fields = buildVoucherFields({
    voucher: shouldAttach ? normalizedVoucher : null,
    couponId: isVerified ? couponId || null : null,
    couponCode: shouldAttach ? (verifiedCodeMatches ? couponCode : normalizedVoucher) : null,
  });

  return {
    ...fields,
    hasInput,
    isVerified,
    isInvalid,
    promotionCodeId: isVerified ? promotionCodeId || null : null,
    isContentVoucher: Boolean(isContentVoucher),
  };
};
